import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
  memo,
} from 'react';
import { bannerApi } from '@/services/api';
import {
  bannerPixelsPerSecond,
  bannerTextTickerDurationSec,
} from '@/utils/bannerScrollSpeed';
import { normalizeExternalUrl } from '@/utils/externalUrl';

const BannerImage = memo(({ banner, height, onTrackClick }) => {
  const [error, setError] = useState(false);
  const href = normalizeExternalUrl(banner.link_url);

  const optimizedUrl = useMemo(() => {
    const url = banner.image_url;
    if (!url) return '';

    if (url.includes('cloudinary.com')) {
      return url.replace('/upload/', '/upload/f_auto,q_auto,w_320,h_80,c_fit/');
    }
    if (url.includes('unsplash.com')) {
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?w=400&h=120&fit=crop&auto=format&q=75`;
    }
    return url;
  }, [banner.image_url]);

  const inner = !error ? (
    <img
      src={optimizedUrl}
      alt={banner.title || 'Partner'}
      loading="eager"
      decoding="async"
      draggable={false}
      onError={() => setError(true)}
      className="h-full w-auto object-contain select-none"
      style={{
        maxHeight: `${height}px`,
        maxWidth: '280px',
      }}
    />
  ) : (
    <div
      className="h-full min-w-[120px] flex items-center justify-center"
      style={{ maxHeight: `${height}px` }}
    >
      <span className="text-white/40 text-xs">{banner.title?.slice(0, 12) || 'Logotyp'}</span>
    </div>
  );

  const className =
    'flex-shrink-0 h-full px-5 md:px-8 flex items-center justify-center' +
    (href ? ' cursor-pointer hover:opacity-90 transition-opacity' : '');

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        title={`Besök ${banner.title || 'partner'}`}
        onClick={() => onTrackClick(banner)}
      >
        {inner}
      </a>
    );
  }

  return <div className={className}>{inner}</div>;
});

BannerImage.displayName = 'BannerImage';

const LogoTrack = memo(({ items, height, scrollDistance, durationSec, onTrackClick }) => {
  if (!items.length || !scrollDistance) return null;

  return (
    <div
      className="flex items-center h-full flex-nowrap banner-marquee-track"
      style={{
        '--banner-scroll-distance': `${scrollDistance}px`,
        '--banner-scroll-duration': `${durationSec}s`,
      }}
    >
      {items.map((banner, index) => (
        <BannerImage
          key={`${banner.id}-${index}`}
          banner={banner}
          height={height}
          onTrackClick={onTrackClick}
        />
      ))}
    </div>
  );
});

LogoTrack.displayName = 'LogoTrack';

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [settings, setSettings] = useState({
    banner_height_desktop: 100,
    banner_height_mobile: 70,
    scroll_speed: 3,
    is_enabled: true,
    banner_title: 'Våra samarbetspartners',
    fallback_scroll_text: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [trackItems, setTrackItems] = useState([]);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [durationSec, setDurationSec] = useState(30);

  const containerRef = useRef(null);
  const measureRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const res = await bannerApi.getActive();
      setBanners(res.data.banners || []);
      setSettings((prev) => ({ ...prev, ...res.data.settings }));
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBannerClick = useCallback((banner) => {
    bannerApi.click(banner.id).catch(() => {});
  }, []);

  const baseList = useMemo(() => {
    if (banners.length === 0) return [];

    const uniqueStores = [...new Set(banners.map((b) => b.store_id))];
    if (uniqueStores.length === 1) return banners;

    return uniqueStores
      .map((storeId) => banners.find((b) => b.store_id === storeId))
      .filter(Boolean);
  }, [banners]);

  const buildTrack = useCallback(() => {
    if (!baseList.length || !measureRef.current || !containerRef.current) return;

    const singleSetWidth = measureRef.current.scrollWidth;
    const containerWidth = containerRef.current.offsetWidth;
    if (!singleSetWidth || !containerWidth) return;

    /* Minst en halv varv ska vara bredare än skärmen så bandet alltid fyller bredden */
    const repeatsPerHalf = Math.max(2, Math.ceil((containerWidth * 1.25) / singleSetWidth));
    const oneHalf = Array.from({ length: repeatsPerHalf }, () => baseList).flat();
    const loopItems = [...oneHalf, ...oneHalf];

    const halfWidth = singleSetWidth * repeatsPerHalf;
    const pixelsPerSecond = bannerPixelsPerSecond(settings.scroll_speed);

    setTrackItems(loopItems);
    setScrollDistance(halfWidth);
    setDurationSec(Math.max(25, halfWidth / pixelsPerSecond));
  }, [baseList, settings.scroll_speed]);

  useLayoutEffect(() => {
    buildTrack();
  }, [buildTrack, isMobile]);

  useEffect(() => {
    const onResize = () => buildTrack();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [buildTrack]);

  useEffect(() => {
    const node = measureRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => buildTrack());
    ro.observe(node);
    return () => ro.disconnect();
  }, [buildTrack, baseList.length]);

  const fallbackText = (settings.fallback_scroll_text || '').trim();
  const hasLogos = banners.length > 0;
  const showTextFallback = !hasLogos && fallbackText.length > 0;

  if (isLoading || !settings.is_enabled) {
    return null;
  }

  if (!hasLogos && !showTextFallback) {
    return null;
  }

  const height = isMobile ? settings.banner_height_mobile : settings.banner_height_desktop;
  const imageHeight = height - (isMobile ? 12 : 16);
  const textDuration = bannerTextTickerDurationSec(settings.scroll_speed);

  return (
    <div className="w-full" data-testid="banner-section">
      {settings.banner_title && (hasLogos || showTextFallback) && (
        <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] py-2 md:py-3 text-center">
          <h3 className="text-white/90 text-xs md:text-sm font-bold tracking-wide uppercase">
            {settings.banner_title}
          </h3>
        </div>
      )}

      {hasLogos && (
        <div
          ref={containerRef}
          className="w-full overflow-hidden bg-gradient-to-r from-[#0F172A] to-[#1E293B] relative"
          style={{ height: `${height}px` }}
          data-testid="banner-carousel"
        >
          {/* Mät bredd på en uppsättning loggor (dold) */}
          <div
            ref={measureRef}
            className="flex items-center h-full flex-nowrap absolute opacity-0 pointer-events-none"
            aria-hidden
            style={{ visibility: 'hidden', height: `${height}px`, left: 0, top: 0 }}
          >
            {baseList.map((banner) => (
              <BannerImage
                key={`measure-${banner.id}`}
                banner={banner}
                height={imageHeight}
                onTrackClick={() => {}}
              />
            ))}
          </div>

          <LogoTrack
            items={trackItems}
            height={imageHeight}
            scrollDistance={scrollDistance}
            durationSec={durationSec}
            onTrackClick={handleBannerClick}
          />
        </div>
      )}

      {showTextFallback && (
        <div
          className="w-full overflow-hidden bg-gradient-to-r from-[#0F172A] to-[#1E293B] relative flex items-center"
          style={{ minHeight: `${Math.max(56, height - 24)}px` }}
          data-testid="banner-text-ticker"
        >
          <div
            className="flex items-center h-full flex-nowrap banner-marquee-track py-3"
            style={{
              '--banner-scroll-distance': '50%',
              '--banner-scroll-duration': `${textDuration}s`,
            }}
          >
            {[fallbackText, fallbackText, fallbackText, fallbackText].map((text, index) => (
              <span
                key={index}
                className="inline-block px-12 md:px-20 text-white text-sm md:text-base font-bold whitespace-nowrap flex-shrink-0"
              >
                {text}
              </span>
            ))}
          </div>
        </div>
      )}

      {(hasLogos || showTextFallback) && (
        <style>{`
          .banner-marquee-track {
            width: max-content;
            will-change: transform;
            transform: translate3d(0, 0, 0);
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            animation: bannerMarquee var(--banner-scroll-duration, 30s) linear infinite;
          }
          @keyframes bannerMarquee {
            0% {
              transform: translate3d(0, 0, 0);
            }
            100% {
              transform: translate3d(calc(-1 * var(--banner-scroll-distance, 50%)), 0, 0);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .banner-marquee-track {
              animation: none !important;
            }
          }
        `}</style>
      )}
    </div>
  );
};

export default memo(BannerCarousel);
